<script setup lang="ts">
import { h, reactive, ref } from 'vue'
import { NCard, NButton, NDataTable, NTag, NFlex, NModal, NIcon, NEmpty } from 'naive-ui'

import DetailsCard from "@/components/utils/DetailsCard.vue";

import { TrashAltRegular, EditRegular, InfoCircle, Exclamation as ExclamationIcon } from '@vicons/fa'

import { useProjectIndexStore } from "@/stores/projects/index.store";
import { useProjectShowStore } from "@/stores/projects/show.store";
import { useProjectDeleteStore } from "@/stores/projects/delete.store";
import { useRouter } from "vue-router";

import type { DataTableColumns } from 'naive-ui'

type ProjectRowData = {
  key: number
  role_title: string
  created_at: string
  status: string
}

const projectStore = useProjectIndexStore()
const projectShowStore = useProjectShowStore()
const projectDeleteStore = useProjectDeleteStore()

const router = useRouter()
const activeDeleteItem = reactive<{ id: number, title: string | null }>({
  id: 0,
  title: null
})

const showDetailsCard = ref<boolean>(false)
const showDeleteModal = ref<boolean>(false)

projectStore.listProjects()

const detail = (rowData: ProjectRowData) => {
  showDetailsCard.value = true
  projectShowStore.getProjectInfo(rowData.key)
}
const edit = (rowData: ProjectRowData) => {
  router.push({ name: 'client-project-edit', params: { id: rowData.key } })
}

const remove = (rowData: ProjectRowData) => {
  activeDeleteItem.id = rowData.key
  activeDeleteItem.title = rowData.role_title
  showDeleteModal.value = true
}

const actions = [
  {
    name: 'Detail',
    icon: InfoCircle,
    action: detail,
    redirect: false
  },
  {
    name: 'Edit',
    icon: EditRegular,
    action: edit,
    redirect: false
  },
  {
    name: 'Delete',
    icon: TrashAltRegular,
    action: remove,
    redirect: false
  },
]

const createColumns = (): DataTableColumns<ProjectRowData> => {
  return [
    {
      title: 'Role title',
      key: 'role_title',
      sorter: 'default',
      className: 'text-w-bold'
    },
    {
      title: 'Date',
      key: 'created_at',
      sorter: 'default',
      className: 'text-pink',
      render (row) {
        const date = new Date(row.created_at)
        return date.toLocaleDateString()
      }
    },
    {
      title: 'Status',
      key: 'status',
      render (row) {
        return h(
          NTag,
          {
            style: {
              marginRight: '6px',
              textTransform: 'capitalize'
            },
            type: 'info',
            bordered: false
          },
          {
            default: () => row.status
          }
        )
      }
    },
    {
      title: 'Action',
      key: 'actions',
      render (row) {
        const buttons = actions.map((action) => {
          return h(
            NButton,
            {
              size: 'medium',
              circle: true,
              quaternary: true,
              onClick: () => action.action(row)
            },
            { icon: () => h(action.icon) }
          )
        })

        return h(
          NFlex,
          {
            size: 'small'
          },
          {
            default : () => {
              return buttons
            }
          }
        )
      }
    }
  ]
}

const columns = createColumns();

const detailsDataStructure = [
  {
    label: 'Role title',
    key: 'role_title',
    type: 'string',
  },
  {
    label: 'Years of experience',
    key: 'years_of_experience',
    type: 'string',
  },
  {
    label: 'Budgeted hourly rate (USD)',
    key: 'usd_hourly_rate',
    type: 'string',
  },
  {
    label: 'Job description',
    key: 'job_description',
    type: 'string',
  },
  {
    label: 'Job description link',
    key: 'job_description_link',
    type: 'link',
  },
  {
    label: 'Main Skills',
    key: 'skills',
    type: 'object',
  },
  {
    label: 'Secondary Skills',
    key: 'complementary_skills',
    type: 'object',
  },
]

const confirmDelete = async () => {
  const response = await projectDeleteStore.deleteProject(activeDeleteItem.id)

  if(response.ok) {
    showDeleteModal.value = false

    projectStore.listProjects()
  }
}

const cancelDelete = () => {
  showDeleteModal.value = false

  setTimeout(() => {
    activeDeleteItem.id = 0
    activeDeleteItem.title = null
  }, 1000)
}
</script>

<template>
  <section class="project-index">
    <div class="grid-container">
      <n-card size="huge" title="Projects" :bordered="false">
        <div class="grid-x button-group">
          <div class="cell small-6 medium-3">
            <n-button type="primary" size="large">
              ACTIVE PROJECTS
            </n-button>
          </div>
          <div class="cell small-6 medium-3">
            <n-button type="primary" size="large" disabled>
              CLOSED PROJECTS
            </n-button>
          </div>
          <div class="cell small-6 medium-3">
            <n-button type="primary" size="large" disabled>
              ALL PROJECTS
            </n-button>
          </div>
          <div class="cell small-6 medium-3">
            <router-link :to="{ name: 'client-project-create' }">
              <n-button size="large">
                &#43; NEW PROJECT
              </n-button>
            </router-link>
          </div>
        </div>
      </n-card>
    </div>
    <div class="grid-container data-table-container">
      <div class="grid-x data-table-grid">
        <div class="cell data-table" :class="showDetailsCard ? 'small-8' : 'small-12'">
          <n-card size="huge" title="Active Projects" :bordered="false">
            <n-data-table
              size="large"
              :bordered="false"
              :columns="columns"
              :data="projectStore.projects"
              :pagination="{ pageSize: 10 }"
            >
              <template #empty>
                <n-empty description="No projects registered" />
              </template>
            </n-data-table>
          </n-card>
        </div>

        <div v-show="showDetailsCard" class="cell card-container" :class="{ 'small-4': showDetailsCard }">
          <details-card title="Project Details" :details-structure="detailsDataStructure" :data="projectShowStore.project" @close-card="showDetailsCard = false" />
        </div>
      </div>
    </div>

    <n-modal
      v-model:show="showDeleteModal"
      preset="dialog"
      :title="`Delete &quot;${activeDeleteItem.title}&quot;`"
      content="Are you sure?"
      positive-text="Delete"
      negative-text="Cancel"
      :loading="projectDeleteStore.status === 0"
      @positive-click="confirmDelete"
      @negative-click="cancelDelete"
    >
      <template #icon>
        <n-icon>
          <exclamation-icon></exclamation-icon>
        </n-icon>
      </template>
    </n-modal>
  </section>
</template>

<style lang="scss">
.project-index {
  .button-group {
    gap: 20px;

    .medium-3 {
      width: calc(25% - 15px);
    }
  }

  .data-table-container {
    margin-top: 25px;

    .data-table-grid {
      gap: 12px;

      .small-8 {
        width: calc(66.6666666667% - 6px);
      }

      .small-4 {
        width: calc(33.3333333334% - 6px);
      }
    }

    .data-table {
      transition: width 150ms ease-in-out;
    }

    .card-container:not(.no-transition) {
      transition: opacity 100ms ease-in-out;
      transition-delay: 150ms;
    }
  }
}

//.n-data-table .n-data-table-td.text-pink {
//  --n-td-text-color: #{$pink};
//}
//
//
//.n-data-table {
//  .n-data-table-tbody {
//    .n-data-table-tr {
//      position: relative;
//      &::after {
//        content: "";
//        position: absolute;
//        bottom: 0;
//        left: 0;
//        width: 100%;
//        height: 2px;
//        background: $border-color-gradient;
//      }
//    }
//  }
//}
</style>
